import { useEffect, useRef, useState } from 'react'
import type { CompanionColour } from '../lib/youthRewards'

type YouthNixiVideoVariant = 'subtle' | 'wave' | 'flying' | 'celebrate' | 'eating'

type YouthNixiClip = {
  src: string
  fallbackSrc?: string
  startAt?: number
  endAt?: number
}

const pinkGirlSources: Record<YouthNixiVideoVariant, YouthNixiClip> = {
  subtle: { src: '/nixi/nixi-girl-pink-subtle-float.mov', fallbackSrc: '/nixi/nixi-youth-home.mp4' },
  wave: { src: '/nixi/nixi-youth-pink-girl-wave.mp4' },
  flying: { src: '/nixi/nixi-girl-pink-subtle-float.mov', fallbackSrc: '/nixi/nixi-youth-home.mp4' },
  celebrate: { src: '/nixi/nixi-girl-pink-celebrate.mp4' },
  eating: { src: '/nixi/nixi-youth-pink-girl-eating.mp4' },
}

const greenBoyMain = '/nixi/nixi-youth-green-boy-main.mp4'
const blueBoyMain = '/nixi/nixi-youth-blue-boy-main.mp4'

const blueBoySources: Record<YouthNixiVideoVariant, YouthNixiClip> = {
  subtle: { src: blueBoyMain, startAt: 0, endAt: 1.5 },
  wave: { src: blueBoyMain, startAt: 1.5, endAt: 3 },
  flying: { src: blueBoyMain, startAt: 3, endAt: 5 },
  celebrate: { src: blueBoyMain, startAt: 5, endAt: 6.95 },
  eating: { src: '/nixi/nixi-youth-blue-boy-eating.mp4' },
}

const greenBoySources: Record<YouthNixiVideoVariant, YouthNixiClip> = {
  subtle: { src: greenBoyMain, startAt: 0, endAt: 1.5 },
  wave: { src: greenBoyMain, startAt: 1.5, endAt: 3 },
  flying: { src: greenBoyMain, startAt: 3, endAt: 5 },
  celebrate: { src: greenBoyMain, startAt: 5, endAt: 6.95 },
  eating: { src: '/nixi/nixi-youth-green-boy-eating.mp4' },
}

const variants: YouthNixiVideoVariant[] = ['subtle', 'wave', 'flying', 'celebrate', 'eating']
const colours: CompanionColour[] = ['pink', 'blue', 'green']
const sourceSets: Record<CompanionColour, Record<YouthNixiVideoVariant, YouthNixiClip>> = {
  pink: pinkGirlSources,
  blue: blueBoySources,
  green: greenBoySources,
}

function videoKey(colour: CompanionColour, variant: YouthNixiVideoVariant) {
  return `${colour}-${variant}`
}

export function YouthNixiVideo({
  className = '',
  label = 'Nixi animation',
  variant = 'subtle',
  colour = 'pink',
  restartToken = 0,
  preloadAllColours = false,
}: {
  className?: string
  label?: string
  variant?: YouthNixiVideoVariant
  colour?: CompanionColour
  restartToken?: number
  preloadAllColours?: boolean
}) {
  const videoRefs = useRef<Record<string, HTMLVideoElement>>({})
  const targetKey = videoKey(colour, variant)
  const targetKeyRef = useRef(targetKey)
  targetKeyRef.current = targetKey
  const [visibleKey, setVisibleKey] = useState(targetKey)
  const visibleKeyRef = useRef(visibleKey)
  const pauseTimerRef = useRef<number | null>(null)
  visibleKeyRef.current = visibleKey
  const renderedColours = preloadAllColours ? [colour, ...colours.filter((item) => item !== colour)] : [colour]
  const renderedVariants = Array.from(new Set<YouthNixiVideoVariant>([variant, 'celebrate', 'eating', ...variants]))

  const revealTarget = (key: string, video: HTMLVideoElement, clip: YouthNixiClip) => {
    if (key !== targetKeyRef.current) return
    const startAt = clip.startAt ?? 0
    if (video.currentTime < startAt || (clip.endAt !== undefined && video.currentTime >= clip.endAt)) {
      video.currentTime = startAt
    }
    setVisibleKey(key)
    visibleKeyRef.current = key
    void video.play().catch(() => undefined)
    if (pauseTimerRef.current !== null) window.clearTimeout(pauseTimerRef.current)
    pauseTimerRef.current = window.setTimeout(() => {
      Object.entries(videoRefs.current).forEach(([otherKey, otherVideo]) => {
        if (otherKey !== targetKeyRef.current) otherVideo.pause()
      })
    }, 340)
  }

  useEffect(() => () => {
    if (pauseTimerRef.current !== null) window.clearTimeout(pauseTimerRef.current)
  }, [])

  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([key, video]) => {
      video.defaultMuted = true
      video.muted = true
      if (video.readyState === HTMLMediaElement.HAVE_NOTHING) video.load()
    })

    const targetVideo = videoRefs.current[targetKey]
    if (!targetVideo) return
    const clip = sourceSets[colour][variant]
    const startAt = clip.startAt ?? 0
    if (Math.abs(targetVideo.currentTime - startAt) > 0.12) targetVideo.currentTime = startAt
    if (targetVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) revealTarget(targetKey, targetVideo, clip)
    else {
      const previousVideo = videoRefs.current[visibleKeyRef.current]
      if (previousVideo && previousVideo !== targetVideo) void previousVideo.play().catch(() => undefined)
      void targetVideo.play().catch(() => undefined)
    }
  }, [colour, variant, restartToken, targetKey])

  return (
    <span
      className={`relative block overflow-hidden ${className}`}
      aria-label={label}
      data-nixi-colour={colour}
      data-nixi-variant={variant}
    >
      {renderedVariants.flatMap((name) => renderedColours.map((renderedColour) => {
        const clip = sourceSets[renderedColour][name]
        const key = videoKey(renderedColour, name)
        const isTarget = key === targetKey
        return (
        <video
          key={key}
          ref={(video) => {
            if (video) videoRefs.current[key] = video
            else delete videoRefs.current[key]
          }}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${key === visibleKey ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          autoPlay={isTarget || !preloadAllColours}
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden={!isTarget}
          onLoadedMetadata={(event) => {
            if (key === targetKeyRef.current) event.currentTarget.currentTime = clip.startAt ?? 0
          }}
          onLoadedData={(event) => {
            event.currentTarget.defaultMuted = true
            event.currentTarget.muted = true
            if (key !== targetKeyRef.current) return
            revealTarget(key, event.currentTarget, clip)
          }}
          onCanPlay={(event) => {
            event.currentTarget.defaultMuted = true
            event.currentTarget.muted = true
            if (key !== targetKeyRef.current) {
              if (key !== visibleKeyRef.current) event.currentTarget.pause()
              return
            }
            revealTarget(key, event.currentTarget, clip)
          }}
          onTimeUpdate={(event) => {
            if (clip.endAt === undefined || event.currentTarget.currentTime < clip.endAt) return
            event.currentTarget.currentTime = clip.startAt ?? 0
            void event.currentTarget.play().catch(() => undefined)
          }}
        >
          <source src={clip.src} type={clip.src.endsWith('.mov') ? undefined : 'video/mp4'} />
          {clip.fallbackSrc && <source src={clip.fallbackSrc} type="video/mp4" />}
        </video>
        )
      }))}
    </span>
  )
}

